import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { MakeDrawerBody } from './_components/MakeDrawerBody';
import { useCatalogMake } from '../../api/admin-vehicle-catalog';
import { httpStatusOf } from '../../api/errors';

/**
 * Make drawer of «Справочник авто» (AUT-437), after
 * `design/mockups/admin/vehicle-catalog-make.html` (drawer pattern from
 * `pickers/PickerDetailDrawer.tsx`). The detail query runs only while
 * `makeId` is set; `syncing` (the detail is refetching) keeps Save and the
 * one-click chips disabled so no PATCH leaves with an `updatedAt` about to
 * move.
 */
const DRAWER_WIDTH = 820;
const width = { xs: '100vw', sm: DRAWER_WIDTH };

export interface MakeDrawerProps {
  makeId: string | null;
  justCreated: boolean;
  onClose: () => void;
  onOpenMake: (makeId: string) => void;
  onRenamed: () => void;
}

export const MakeDrawer = ({
  makeId,
  justCreated,
  onClose,
  onOpenMake,
  onRenamed,
}: MakeDrawerProps): ReactElement => {
  const { t } = useTranslation();
  const query = useCatalogMake(makeId);
  const data = makeId !== null && query.data?.id === makeId ? query.data : undefined;
  const notFound = httpStatusOf(query.error) === 404;

  return (
    <Drawer anchor="right" open={makeId !== null} onClose={onClose}>
      <Box sx={{ width, maxWidth: '100vw', height: '100%' }}>
        {query.isLoading ? (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
          >
            <CircularProgress />
          </Box>
        ) : null}
        {query.error && !data ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              {notFound
                ? t('vehicleCatalog.drawer.notFound')
                : t('vehicleCatalog.drawer.loadError')}
            </Alert>
          </Box>
        ) : null}
        {data ? (
          <MakeDrawerBody
            key={data.id}
            make={data}
            syncing={query.isFetching}
            justCreated={justCreated}
            onClose={onClose}
            onOpenMake={onOpenMake}
            onNotFound={onClose}
            onRenamed={onRenamed}
          />
        ) : null}
      </Box>
    </Drawer>
  );
};
